import { useCallback, useEffect, useRef } from 'react';
import { captureContact } from '../lib/api';

/**
 * Robustly captures the visitor's email + phone to MongoDB on the IQ /
 * Personality paywalls. The "click Unlock My Results" button is no longer the
 * only chance to save — we also save:
 *   1. On mount, if the user already has an email (already-logged-in users).
 *   2. When the email field loses focus and contains a valid address.
 *   3. As a sendBeacon on pagehide / visibilitychange=hidden so even users
 *      who close the tab still have their email recorded.
 *
 * Internally dedupes — the same {email, phone} pair is sent at most once.
 *
 * Usage:
 *   const { onEmailBlur } = useContactAutoCapture({ deviceId, email, phone, source });
 *   <InlineContactFields ... onEmailBlur={onEmailBlur} />
 */
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function useContactAutoCapture({ deviceId, email, phone, source }) {
  const lastSentRef = useRef(''); // dedupe key: `${email}|${phone||''}`
  const inFlightRef = useRef(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const buildPayload = useCallback(() => {
    const e = (email || '').trim().toLowerCase();
    const p = (phone || '').trim();
    if (!e || !EMAIL_RX.test(e)) return null;
    return {
      device_id: deviceId,
      email: e,
      phone: p || null,
      source,
    };
  }, [deviceId, email, phone, source]);

  const dedupeKey = useCallback((payload) => `${payload.email}|${payload.phone || ''}`, []);

  const send = useCallback(async () => {
    const payload = buildPayload();
    if (!payload) return;
    const key = dedupeKey(payload);
    if (lastSentRef.current === key || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await captureContact(payload);
      lastSentRef.current = key;
    } catch {
      /* network glitch — try again on next trigger */
    } finally {
      inFlightRef.current = false;
    }
  }, [buildPayload, dedupeKey]);

  // 1. Capture on mount if email is already populated (logged-in users)
  useEffect(() => {
    const payload = buildPayload();
    if (payload) {
      // schedule a tick later so we don't block render
      const t = setTimeout(send, 0);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Capture on pagehide / visibilitychange via sendBeacon. This is the
  // failsafe — even tabs that close before the user clicks anything will fire.
  useEffect(() => {
    const fire = () => {
      const payload = buildPayload();
      if (!payload) return;
      const key = dedupeKey(payload);
      if (lastSentRef.current === key) return;
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon?.(`${apiBase}/api/users/contact`, blob);
        if (ok) lastSentRef.current = key;
      } catch {
        /* ignore */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') fire();
    };
    window.addEventListener('pagehide', fire);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', fire);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [buildPayload, dedupeKey, apiBase]);

  // 3. Imperative trigger consumers wire to onBlur of the email input
  const onEmailBlur = useCallback(() => { send(); }, [send]);

  return { onEmailBlur, send };
}
